import { useCallback, useEffect, useState } from "react";
import { KpiSkeleton, ListSkeleton } from "../components/Skeletons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft, Loader2, Users, Calendar, ClipboardList, FileText,
  Mail, Plus, Check, X, Copy, Send, Signature, Trash2, Download,
  CheckCircle2, Clock, MapPin, GraduationCap, ShieldCheck, AlertTriangle,
  Activity, FileDown, MessageCircle, UserPlus,
} from "lucide-react";
import SessionChat from "../components/SessionChat";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PageHeader from "../components/PageHeader";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";

const TABS = [
  { k: "stagiaires", l: "Stagiaires", icon: Users },
  { k: "formateurs", l: "Formateurs", icon: UserPlus },
  { k: "emargements", l: "Emargements", icon: Calendar },
  { k: "modules", l: "Modules e-learning", icon: GraduationCap },
  { k: "evaluations", l: "Evaluations", icon: ClipboardList },
  { k: "documents", l: "Documents Qualiopi", icon: FileText },
  { k: "relances", l: "Relances", icon: Mail },
  { k: "chat", l: "Messagerie", icon: MessageCircle },
  { k: "suivi", l: "Suivi", icon: Activity },
  { k: "conformite", l: "Conformite", icon: ShieldCheck },
];

export default function SessionPilotagePage() {
  const { session_id } = useParams();
  const navigate = useNavigate();
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stagiaires");
  const [enrollments, setEnrollments] = useState([]);
  const [emargements, setEmargements] = useState([]);
  const [linkedContent, setLinkedContent] = useState({ modules: [], evaluations: [], doc_templates: [] });
  const [qualiopiDocs, setQualiopiDocs] = useState([]);
  const [relances, setRelances] = useState([]);
  const [sigRequests, setSigRequests] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get("/lms/sessions/" + session_id);
      setSession(s.data);
      const [e, em, q, rel, sg] = await Promise.all([
        api.get("/lms/sessions/" + session_id + "/enrollments").catch(() => ({ data: [] })),
        api.get("/lms/sessions/" + session_id + "/emargements").catch(() => ({ data: [] })),
        api.get("/lms/qualiopi/documents", { params: { session_id } }).catch(() => ({ data: [] })),
        api.get("/lms/sessions/" + session_id + "/relances").catch(() => ({ data: [] })),
        api.get("/lms/sessions/" + session_id + "/signature-requests").catch(() => ({ data: [] })),
      ]);
      setEnrollments(e.data || []);
      setEmargements(em.data || []);
      setQualiopiDocs(q.data || []);
      setRelances(rel.data || []);
      setSigRequests(sg.data || []);
      if (s.data?.program_id) {
        try {
          const l = await api.get("/lms/programs/" + s.data.program_id + "/links");
          setLinkedContent({
            modules: l.data.modules || [],
            evaluations: l.data.evaluations || [],
            doc_templates: l.data.doc_templates || [],
          });
        } catch { /* silent */ }
      }
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [session_id]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [session_id]);

  if (loading || !session) {
    return <div className="p-6 lg:p-10 space-y-6" data-testid="session-pilotage-loading"><KpiSkeleton count={4} /><ListSkeleton items={3} /></div>;
  }

  const today = new Date().toISOString().slice(0, 10);
  const status = session.end_date < today ? "terminee" : session.start_date > today ? "a_venir" : "en_cours";
  const statusMeta = { a_venir: { label: "A venir", color: "#3b82f6" }, en_cours: { label: "En cours", color: "#10b981" }, terminee: { label: "Terminee", color: "#6b7280" } }[status];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="session-piloting-page">
      <PageHeader
        icon={<GraduationCap />}
        title={session.title}
        description={(
          <span className="flex items-center gap-3 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded font-bold" style={{ background: `${statusMeta.color}20`, color: statusMeta.color }}>{statusMeta.label}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{session.start_date} → {session.end_date}</span>
            {session.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{session.location}</span>}
            {session.trainer_name && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{session.trainer_name}</span>}
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{session.program_duration_hours || 0}h</span>
            <span className="flex items-center gap-1">{enrollments.length} / {session.max_participants} stagiaires</span>
          </span>
        )}
        breadcrumb={[{ label: "Formation" }, { label: "Sessions" }, { label: session.title }]}
        testId="session-piloting-header"
        actions={(
          <Button variant="ghost" onClick={() => navigate("/admin/formation/sessions")} className="text-slate-400 hover:bg-white/5" data-testid="back-sessions">
            <ArrowLeft className="w-4 h-4 mr-1" /> Sessions
          </Button>
        )}
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.k;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={"pilot-tab-" + t.k}
              className={"flex items-center gap-2 px-4 py-2.5 text-xs font-bold whitespace-nowrap transition-colors border-b-2 -mb-px " + (active ? "border-[#f7c935] text-[#f7c935]" : "border-transparent text-slate-400 hover:text-white")}>
              <Icon className="w-3.5 h-3.5" /> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "stagiaires" && <StagiairesTab session={session} enrollments={enrollments} api={api} reload={load} />}
      {tab === "formateurs" && <FormateursTab session={session} api={api} />}
      {tab === "emargements" && <EmargementsTab session={session} emargements={emargements} enrollments={enrollments} api={api} reload={load} />}
      {tab === "modules" && <ModulesTab modules={linkedContent.modules} />}
      {tab === "evaluations" && <EvaluationsTab evaluations={linkedContent.evaluations} />}
      {tab === "documents" && <DocumentsTab session={session} docs={qualiopiDocs} sigRequests={sigRequests} api={api} reload={load} />}
      {tab === "relances" && <RelancesTab session={session} enrollments={enrollments} relances={relances} api={api} reload={load} />}
      {tab === "chat" && <SessionChat sessionId={session.session_id} api={api} currentUser={user} />}
      {tab === "suivi" && <SuiviTab session={session} api={api} />}
      {tab === "conformite" && <ConformiteTab session={session} api={api} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
const STATUS_BADGE = {
  devis_envoye: { label: "Devis", color: "#f59e0b" },
  devis_signe: { label: "Devis signe", color: "#8b5cf6" },
  acompte_recu: { label: "Acompte", color: "#06b6d4" },
  inscrit: { label: "Inscrit", color: "#10b981" },
  en_cours: { label: "En cours", color: "#3b82f6" },
  termine: { label: "Termine", color: "#6b7280" },
  defaut: { label: "Defaut", color: "#ef4444" },
  annule: { label: "Annule", color: "#ef4444" },
  en_attente: { label: "En attente", color: "#f59e0b" },
};

const PAYMENT_LABELS = {
  comptant: "Comptant", subrogation: "Subrogation", credit: "Credit",
  cpf: "CPF", opco: "OPCO", autre: "Autre",
};

function StagiairesTab({ session, enrollments, api, reload }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ student_name: "", student_email: "", student_phone: "", student_company: "", payment_method: "", financeur_name: "", financeur_siret: "" });

  const add = async () => {
    try {
      await api.post("/lms/sessions/" + session.session_id + "/enrollments", form);
      toast.success("Inscription enregistree");
      setShowAdd(false);
      setForm({ student_name: "", student_email: "", student_phone: "", student_company: "", payment_method: "", financeur_name: "", financeur_siret: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const confirm = async (enr) => {
    try {
      await api.put("/lms/enrollments/" + enr.enrollment_id + "/confirm");
      toast.success("Inscription confirmee");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const cancel = async (enr) => {
    if (!window.confirm(`Annuler l'inscription de ${enr.student_name} ?`)) return;
    try {
      await api.put("/lms/enrollments/" + enr.enrollment_id + "/cancel");
      toast.success("Inscription annulee");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const updateStatus = async (enr, newStatus) => {
    try {
      await api.put("/lms/enrollments/" + enr.enrollment_id, { status: newStatus });
      toast.success("Statut mis a jour");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const PIPELINE_STATUSES = ["devis_envoye", "devis_signe", "acompte_recu", "en_attente"];
  const SEAT_STATUSES = ["inscrit", "en_cours", "termine", "acompte_recu", "devis_signe"];
  const pipelineCount = enrollments.filter((e) => PIPELINE_STATUSES.includes(e.status)).length;
  const confirmedCount = enrollments.filter((e) => SEAT_STATUSES.includes(e.status)).length;

  return (
    <div>
      <div className="flex justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-white">{enrollments.length} stagiaire{enrollments.length > 1 ? "s" : ""}</h3>
          <div className="text-[10px] text-slate-500 mt-0.5">
            <span className="text-green-400">{confirmedCount} confirme{confirmedCount > 1 ? "s" : ""}</span>
            {pipelineCount > 0 && <span className="text-amber-400 ml-3">{pipelineCount} dossier{pipelineCount > 1 ? "s" : ""} a finaliser</span>}
          </div>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid="enroll-add-btn"><Plus className="w-3 h-3 mr-1" />Inscrire</Button>
      </div>
      {enrollments.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucun stagiaire inscrit</div>
      ) : (
        <div className="bg-[#0b0811] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-[10px] uppercase text-slate-500">
                <th className="px-3 py-2">Nom</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Entreprise</th><th className="px-3 py-2">Paiement</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Devis</th><th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => {
                const sb = STATUS_BADGE[e.status] || STATUS_BADGE.inscrit;
                return (
                  <tr key={e.enrollment_id} className="border-t border-white/5" data-testid={"enroll-row-" + e.enrollment_id}>
                    <td className="px-3 py-2 text-white font-semibold">{e.student_name}</td>
                    <td className="px-3 py-2 text-slate-300">{e.student_email}</td>
                    <td className="px-3 py-2 text-slate-300">{e.student_company || "-"}</td>
                    <td className="px-3 py-2 text-slate-300 text-[11px]">
                      {PAYMENT_LABELS[e.payment_method] || "-"}
                      {e.financeur_name && <div className="text-[10px] text-slate-500">{e.financeur_name}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <select value={e.status} onChange={(ev) => updateStatus(e, ev.target.value)} className="bg-[#14101c] border border-white/10 text-xs rounded px-1.5 py-1" style={{ color: sb.color, borderColor: sb.color + "40" }} data-testid={"enroll-status-" + e.enrollment_id}>
                        {Object.entries(STATUS_BADGE).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-slate-400 text-[11px]">{e.devis_ref || "-"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {PIPELINE_STATUSES.includes(e.status) && (
                        <button onClick={() => confirm(e)} className="text-xs text-green-400 hover:text-green-300 mr-3" data-testid={"enroll-confirm-" + e.enrollment_id} title="Confirmer l'inscription">
                          <Check className="w-3.5 h-3.5 inline-block" /> Confirmer
                        </button>
                      )}
                      {e.status !== "annule" && (
                        <button onClick={() => cancel(e)} className="text-xs text-red-400 hover:text-red-300" data-testid={"enroll-cancel-" + e.enrollment_id} title="Annuler">
                          <X className="w-3.5 h-3.5 inline-block" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {showAdd && (
        <Dialog open onOpenChange={(o) => { if (!o) setShowAdd(false); }}>
          <DialogContent className="bg-[#0b0811] border-white/10 text-white">
            <DialogHeader><DialogTitle>Inscrire un stagiaire</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Nom complet *" value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} className={inputCls} data-testid="enroll-name" />
              <Input placeholder="Email *" type="email" value={form.student_email} onChange={(e) => setForm({ ...form, student_email: e.target.value })} className={inputCls} data-testid="enroll-email" />
              <Input placeholder="Telephone" value={form.student_phone} onChange={(e) => setForm({ ...form, student_phone: e.target.value })} className={inputCls} />
              <Input placeholder="Entreprise" value={form.student_company} onChange={(e) => setForm({ ...form, student_company: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-3 gap-2">
                <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className={inputCls + " px-2 py-2 rounded text-xs"} data-testid="enroll-payment">
                  <option value="">Modalite…</option>
                  <option value="comptant">Comptant</option>
                  <option value="subrogation">Subrogation</option>
                  <option value="credit">Credit</option>
                  <option value="cpf">CPF</option>
                  <option value="opco">OPCO</option>
                  <option value="autre">Autre</option>
                </select>
                <Input placeholder="Financeur" value={form.financeur_name} onChange={(e) => setForm({ ...form, financeur_name: e.target.value })} className={inputCls + " text-xs h-9"} />
                <Input placeholder="SIRET financeur" value={form.financeur_siret} onChange={(e) => setForm({ ...form, financeur_siret: e.target.value })} className={inputCls + " text-xs h-9"} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3"><Button variant="ghost" onClick={() => setShowAdd(false)}>Annuler</Button><Button onClick={add} disabled={!form.student_name || !form.student_email} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="enroll-save">Inscrire</Button></div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EmargementsTab({ session, emargements, enrollments, api, reload }) {
  const autoGen = async () => {
    try {
      const r = await api.post("/lms/sessions/" + session.session_id + "/emargements/auto-generate", {});
      if ((r.data.created || []).length === 0) toast.info(r.data.message || "Deja genere");
      else toast.success(`${r.data.created.length} journee(s) d'emargement genere(s)`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggle = async (em, recIdx, present) => {
    const records = [...em.records];
    records[recIdx] = { ...records[recIdx], present, signature_at: present ? new Date().toISOString() : null };
    try {
      await api.put("/lms/sessions/" + session.session_id + "/emargements/" + em.emargement_id, { records });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async (em) => {
    if (!window.confirm("Supprimer cette feuille d'emargement ?")) return;
    try {
      await api.delete("/lms/sessions/" + session.session_id + "/emargements/" + em.emargement_id);
      toast.success("Supprime");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div>
      <div className="flex justify-between mb-3"><h3 className="text-sm font-bold text-white">{emargements.length} feuille{emargements.length > 1 ? "s" : ""} d'emargement</h3>
        <Button size="sm" onClick={autoGen} className="bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid="emarg-auto-btn"><Plus className="w-3 h-3 mr-1" />Generer auto (toutes les journees)</Button>
      </div>
      {emargements.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucun emargement — generez-les automatiquement.</div>
      ) : enrollments.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-amber-400 text-sm">Inscrivez d'abord des stagiaires.</div>
      ) : (
        <div className="space-y-4">
          {emargements.map((em) => (
            <div key={em.emargement_id} className="bg-[#0b0811] border border-white/10 rounded-xl p-4" data-testid={"emarg-" + em.emargement_id}>
              <div className="flex items-center justify-between mb-3">
                <div><div className="font-bold text-white">{em.date} · {em.period === "am" ? "Matin" : em.period === "pm" ? "Apres-midi" : "Journee"} · {em.hours}h</div>{em.trainer_name && <div className="text-[10px] text-slate-500">Formateur: {em.trainer_name}</div>}</div>
                <button onClick={() => del(em)} className="text-red-400 hover:text-red-300" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
              </div>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[10px] uppercase text-slate-500"><th className="py-2">Stagiaire</th><th className="py-2 text-center">Present</th><th className="py-2 text-center">Absent</th><th className="py-2 text-slate-500">Note</th></tr></thead>
                <tbody>
                  {(em.records || []).map((rec, ri) => (
                    <tr key={ri} className="border-t border-white/5">
                      <td className="py-2 text-white">{rec.student_name}</td>
                      <td className="py-2 text-center"><button onClick={() => toggle(em, ri, true)} className={"p-1 rounded " + (rec.present === true ? "bg-green-500/20 text-green-400" : "text-slate-500 hover:bg-white/5")} data-testid={`em-${em.emargement_id}-r${ri}-present`}><Check className="w-4 h-4" /></button></td>
                      <td className="py-2 text-center"><button onClick={() => toggle(em, ri, false)} className={"p-1 rounded " + (rec.present === false ? "bg-red-500/20 text-red-400" : "text-slate-500 hover:bg-white/5")} data-testid={`em-${em.emargement_id}-r${ri}-absent`}><X className="w-4 h-4" /></button></td>
                      <td className="py-2 text-slate-500 text-[10px]">{rec.signature_at ? new Date(rec.signature_at).toLocaleString("fr-FR") : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ModulesTab({ modules }) {
  if (!modules.length) return <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucun module e-learning rattache au programme de cette session.</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="modules-tab">
      {modules.map((m) => (
        <div key={m.module_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4">
          <div className="font-bold text-white text-sm">{m.title}</div>
          <div className="text-xs text-slate-400 mt-1 line-clamp-2">{m.description}</div>
          <div className="text-[10px] text-slate-500 mt-2">{(m.blocks || []).length} blocs · {m.duration_min || 0} min</div>
        </div>
      ))}
    </div>
  );
}

function EvaluationsTab({ evaluations }) {
  if (!evaluations.length) return <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucune evaluation rattachee au programme de cette session.</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="evaluations-tab">
      {evaluations.map((e) => (
        <div key={e.template_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4">
          <div className="font-bold text-white text-sm">{e.title}</div>
          <div className="text-[10px] text-slate-500 mt-1">{e.eval_type || "-"} · {(e.questions || []).length} questions</div>
        </div>
      ))}
    </div>
  );
}

function DocumentsTab({ session, docs, sigRequests, api, reload }) {
  const [genLoading, setGenLoading] = useState(false);
  const [sigDialog, setSigDialog] = useState(null); // { doc_id }

  const gen = async () => {
    setGenLoading(true);
    try {
      const r = await api.post("/lms/sessions/" + session.session_id + "/generate-qualiopi-docs", {});
      toast.success(`${r.data.generated_count} PDF generes`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setGenLoading(false);
  };

  const genAttestations = async () => {
    setGenLoading(true);
    try {
      const r = await api.post("/lms/sessions/" + session.session_id + "/attestations/auto-generate", { signed_by_me: true });
      toast.success(`${r.data.generated_count} attestations signees generees${r.data.skipped_count ? ` (${r.data.skipped_count} ignorees)` : ""}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setGenLoading(false);
  };

  const requestSig = async (doc_id, role) => {
    try {
      const r = await api.post("/lms/qualiopi/documents/" + doc_id + "/request-signature", { role });
      const link = window.location.origin + r.data.url;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success("Lien copie dans le presse-papier — a envoyer au signataire");
      setSigDialog(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const download = async (doc) => {
    try {
      const r = await api.get("/lms/qualiopi/documents/" + doc.document_id + "/download");
      if (r.data?.url) window.open(r.data.url, "_blank");
      else toast.info("URL indisponible");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div>
      <div className="flex justify-between mb-3"><h3 className="text-sm font-bold text-white">{docs.length} document{docs.length > 1 ? "s" : ""} Qualiopi</h3>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={genAttestations} disabled={genLoading} variant="outline" className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-xs" data-testid="gen-attestations-btn">
            <CheckCircle2 className="w-3 h-3 mr-1" />{genLoading ? "…" : "Generer attestations fin de formation"}
          </Button>
          <Button size="sm" onClick={gen} disabled={genLoading} className="bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid="docs-regen"><FileText className="w-3 h-3 mr-1" />{genLoading ? "…" : "Regenerer docs Qualiopi"}</Button>
        </div>
      </div>
      {docs.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucun document — cliquez sur "Regenerer".</div>
      ) : (
        <div className="space-y-2" data-testid="docs-list">
          {docs.map((d) => {
            const sigForDoc = (sigRequests || []).filter((s) => s.document_id === d.document_id);
            const signed = (d.signed_by_roles || []).length > 0 || sigForDoc.some((s) => s.status === "signed");
            return (
              <div key={d.document_id} className="bg-[#14101c] border border-white/5 rounded-xl p-3 flex items-center gap-3" data-testid={"doc-" + d.document_id}>
                <div className="w-10 h-10 rounded bg-[#f7c935]/20 border border-[#f7c935]/40 flex items-center justify-center"><FileText className="w-5 h-5 text-[#f7c935]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-sm truncate">{d.doc_name}</div>
                  <div className="text-[10px] text-slate-500">{d.doc_type} · {new Date(d.created_at).toLocaleDateString("fr-FR")}</div>
                  {sigForDoc.length > 0 && <div className="text-[10px] text-slate-400 mt-1">Signatures: {sigForDoc.map((s) => `${s.role}${s.status === "signed" ? " ✓" : " (en attente)"}`).join(" · ")}</div>}
                </div>
                {signed && <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 font-bold uppercase flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Signe</span>}
                <Button size="sm" variant="ghost" onClick={() => setSigDialog({ doc_id: d.document_id, doc_name: d.doc_name })} className="text-slate-300" data-testid={"doc-sign-" + d.document_id}><Signature className="w-3.5 h-3.5 mr-1" />Signature</Button>
                {d.pdf_url && <Button size="sm" variant="ghost" onClick={() => download(d)} className="text-slate-300"><Download className="w-3.5 h-3.5" /></Button>}
              </div>
            );
          })}
        </div>
      )}
      {sigDialog && (
        <Dialog open onOpenChange={(o) => { if (!o) setSigDialog(null); }}>
          <DialogContent className="bg-[#0b0811] border-white/10 text-white">
            <DialogHeader><DialogTitle>Demande de signature electronique</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-400 mb-3">Genere un lien public (valide 14 jours) a envoyer au signataire. Au clic, il tape son nom → signature horodatee enregistree.</p>
            <div className="text-xs text-white mb-3 font-semibold">{sigDialog.doc_name}</div>
            <div className="grid grid-cols-2 gap-2">
              {["stagiaire", "formateur", "organisme", "employeur", "financeur"].map((r) => (
                <Button key={r} variant="ghost" onClick={() => requestSig(sigDialog.doc_id, r)} className="border border-white/10 hover:border-[#f7c935]/40 capitalize" data-testid={"sig-role-" + r}>{r}</Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function RelancesTab({ session, enrollments, relances, api, reload }) {
  const [showCompose, setShowCompose] = useState(false);
  const [form, setForm] = useState({ type: "custom", subject: "", body: "" });

  const send = async () => {
    if (!form.subject || !form.body) { toast.error("Sujet et message requis"); return; }
    try {
      const r = await api.post("/lms/sessions/" + session.session_id + "/relances", form);
      const recipients = (r.data.recipients || []).map((x) => x.email).filter(Boolean).join(",");
      if (recipients) {
        window.location.href = `mailto:?bcc=${encodeURIComponent(recipients)}&subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(form.body)}`;
      }
      toast.success(`Relance logguee (${(r.data.recipients || []).length} destinataires)`);
      setShowCompose(false);
      setForm({ type: "custom", subject: "", body: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const quickTpl = (kind) => {
    if (kind === "eval_chaud") setForm({ type: "eval_a_chaud", subject: `[Formation] Merci de remplir votre evaluation a chaud - ${session.title}`, body: `Bonjour,\n\nVotre formation "${session.title}" vient de se terminer. Merci de prendre 3 minutes pour remplir l'evaluation a chaud.\n\nCordialement,` });
    else if (kind === "convocation") setForm({ type: "convocation", subject: `[Formation] Convocation - ${session.title}`, body: `Bonjour,\n\nVotre formation "${session.title}" debute le ${session.start_date} a ${session.location || "lieu a confirmer"}.\n\nCordialement,` });
    setShowCompose(true);
  };

  return (
    <div>
      <div className="flex justify-between mb-3"><h3 className="text-sm font-bold text-white">{relances.length} relance{relances.length > 1 ? "s" : ""}</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => quickTpl("convocation")} className="text-slate-300 text-xs" data-testid="relance-convocation">Convocation</Button>
          <Button size="sm" variant="ghost" onClick={() => quickTpl("eval_chaud")} className="text-slate-300 text-xs" data-testid="relance-eval">Eval a chaud</Button>
          <Button size="sm" onClick={() => { setForm({ type: "custom", subject: "", body: "" }); setShowCompose(true); }} className="bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid="relance-new"><Send className="w-3 h-3 mr-1" />Composer</Button>
        </div>
      </div>
      {relances.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucune relance envoyee</div>
      ) : (
        <div className="space-y-2">
          {relances.map((r) => (
            <div key={r.relance_id} className="bg-[#14101c] border border-white/5 rounded-xl p-3">
              <div className="flex items-center justify-between"><div className="font-bold text-white text-sm">{r.subject}</div><span className="text-[10px] text-slate-500">{new Date(r.created_at).toLocaleString("fr-FR")}</span></div>
              <div className="text-xs text-slate-400 mt-1">{(r.recipients || []).length} destinataires · par {r.sent_by_name}</div>
              <div className="text-xs text-slate-300 mt-2 whitespace-pre-wrap">{r.body}</div>
            </div>
          ))}
        </div>
      )}
      {showCompose && (
        <Dialog open onOpenChange={(o) => { if (!o) setShowCompose(false); }}>
          <DialogContent className="max-w-xl bg-[#0b0811] border-white/10 text-white">
            <DialogHeader><DialogTitle>Composer une relance</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-[10px] text-slate-500">{enrollments.length} destinataire{enrollments.length > 1 ? "s" : ""}: {enrollments.map((e) => e.student_email).filter(Boolean).join(", ") || "Aucun email"}</div>
              <Input placeholder="Sujet *" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="relance-subject" />
              <Textarea placeholder="Message *" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={8} className={inputCls} data-testid="relance-body" />
              <div className="text-[10px] text-slate-500">Au clic, la relance sera loggee + ouvrira votre client mail avec les destinataires en BCC.</div>
            </div>
            <div className="flex justify-end gap-2 mt-3"><Button variant="ghost" onClick={() => setShowCompose(false)}>Annuler</Button><Button onClick={send} disabled={!form.subject || !form.body} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="relance-send"><Send className="w-4 h-4 mr-2" />Envoyer</Button></div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
const SEVERITY_COLORS = { critical: "#ef4444", high: "#f97316", medium: "#f59e0b", low: "#6b7280" };
const STATUS_COLORS = { ok: "#10b981", warn: "#f59e0b", ko: "#ef4444" };
const STATUS_LABELS = { ok: "Conforme", warn: "A corriger", ko: "Non conforme" };

function KpiRate({ label, value, target, format, hint, color = "#f7c935" }) {
  const pct = value === null || value === undefined ? null : Number(value);
  const display = pct === null ? "—" : (format === "pct" ? `${pct}%` : (format === "score" ? `${pct}/5` : pct));
  const ok = target && pct !== null ? pct >= target : null;
  const barColor = ok === null ? color : (ok ? "#10b981" : pct >= (target * 0.7) ? "#f59e0b" : "#ef4444");
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl p-4" data-testid={`kpi-rate-${label.replace(/\s+/g,"-").toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
      <div className="text-2xl font-black text-white">{display}</div>
      {target && pct !== null && (
        <>
          <div className="mt-2 bg-white/5 rounded-full h-1.5 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (pct / (format === "score" ? 5 : 100)) * 100)}%`, background: barColor }} />
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Cible: {format === "score" ? `${target}/5` : `${target}%`}{hint ? ` · ${hint}` : ""}</div>
        </>
      )}
      {!target && hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function SuiviTab({ session, api }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lms/sessions/" + session.session_id + "/monitoring");
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [session.session_id]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading || !data) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>;

  return (
    <div data-testid="suivi-tab">
      <div className="mb-4">
        <h3 className="text-sm font-bold text-white">Indicateurs de suivi Qualiopi</h3>
        <p className="text-[10px] text-slate-500">Statistiques obligatoires pour le bilan pedagogique et l'audit.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
        <KpiRate label="Presence" value={data.presence?.rate} target={80} format="pct" hint={`${data.presence?.present_cells || 0} presents / ${data.presence?.absent_cells || 0} absents`} />
        <KpiRate label="Completion e-learning" value={data.completion?.completion_rate} target={80} format="pct" hint={`${data.completion?.completed_count || 0}/${data.enrollments_confirmed || 0} termines`} />
        <KpiRate label="Progression moyenne" value={data.completion?.avg_progress} target={70} format="pct" />
        <KpiRate label="Reponses evaluations" value={data.evaluations?.response_rate} target={70} format="pct" hint={`${data.evaluations?.responses || 0} reponses`} />
        <KpiRate label="Satisfaction (a chaud)" value={data.evaluations?.satisfaction_avg} target={4} format="score" />
        <KpiRate label="NPS" value={data.evaluations?.nps} format="pct" hint="Net Promoter Score" />
        <KpiRate label="Signatures docs" value={data.signatures?.rate} target={90} format="pct" hint={`${data.signatures?.signed_docs || 0}/${data.signatures?.required_docs || 0} signes`} />
        <KpiRate label="Quiz moyen" value={data.quizzes?.avg_score_pct} target={70} format="pct" hint={`${data.quizzes?.attempts || 0} tentatives`} />
      </div>

      <div className="bg-[#0b0811] border border-white/10 rounded-xl p-4">
        <h4 className="text-sm font-bold text-white mb-2">Synthese pour l'audit Qualiopi</h4>
        <ul className="text-xs text-slate-300 space-y-1">
          <li>• <b>{data.enrollments_confirmed}</b> stagiaire(s) confirme(s) suivi(s) sur cette session</li>
          <li>• <b>{data.presence?.total_cells || 0}</b> presences enregistrees ({data.presence?.unmarked_cells || 0} non marquees)</li>
          <li>• <b>{data.completion?.completed_count || 0}</b> formation(s) 100% terminee(s), progression moyenne {data.completion?.avg_progress}%</li>
          <li>• <b>{data.evaluations?.responses || 0}</b> evaluation(s) soumise(s){data.evaluations?.satisfaction_avg ? ` (note moyenne ${data.evaluations.satisfaction_avg}/5)` : ""}</li>
          <li>• <b>{data.signatures?.signed_docs || 0}</b>/<b>{data.signatures?.required_docs || 0}</b> documents signes</li>
        </ul>
        <p className="text-[10px] text-slate-600 mt-3">Ces donnees sont agregees en temps reel depuis les emargements, les reponses e-learning et les signatures electroniques.</p>
      </div>
    </div>
  );
}

function ConformiteTab({ session, api }) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [auditMode, setAuditMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lms/sessions/" + session.session_id + "/compliance-check");
      setReport(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [session.session_id]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportAudit = () => {
    if (!report) return;
    // Generate Markdown -> download as .md (simple, printable)
    const lines = [];
    lines.push(`# Rapport d'audit Qualiopi — ${report.session_title || session.session_id}`);
    lines.push("");
    lines.push(`**Session** : ${session.session_id}`);
    lines.push(`**Date du rapport** : ${new Date().toISOString().slice(0, 10)}`);
    lines.push(`**Score global** : ${report.score}/100 — ${report.score_label}`);
    lines.push(`**Indicateurs conformes** : ${report.ok_count}/${report.total_indicators}`);
    lines.push("");
    lines.push(`## Statistiques`);
    lines.push(`- Stagiaires confirmes : ${report.stats.enrollments_confirmed}`);
    lines.push(`- Documents generes : ${report.stats.docs_total}`);
    lines.push(`- Emargements : ${report.stats.emargements_count}`);
    lines.push(`- Reponses evaluation : ${report.stats.eval_responses}`);
    lines.push("");
    lines.push(`## Indicateurs detailles`);
    for (const ind of report.indicators) {
      lines.push(`### ${ind.code} — ${ind.name}`);
      lines.push(`**Severite** : ${ind.severity.toUpperCase()} | **Statut** : ${STATUS_LABELS[ind.status]}`);
      lines.push(`Documents requis : ${ind.docs_required_types.join(", ")}`);
      lines.push(`Documents trouves : ${ind.docs_found}`);
      if (ind.issues.length) {
        lines.push(`**Problemes** :`);
        for (const iss of ind.issues) lines.push(`- ${iss}`);
      }
      lines.push("");
    }
    if (report.action_items.length) {
      lines.push(`## Actions recommandees (${report.action_items.length})`);
      for (const a of report.action_items) {
        lines.push(`- **[${a.severity.toUpperCase()}]** (${a.indicator}) ${a.action}`);
        if (a.auto_fix) lines.push(`  - Auto-fix : \`${a.auto_fix}\``);
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `audit-qualiopi-${session.session_id}.md`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Rapport d'audit exporte");
  };

  if (loading || !report) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>;

  const scoreColor = report.score >= 90 ? "#10b981" : report.score >= 60 ? "#f59e0b" : "#ef4444";
  const filteredIndicators = auditMode
    ? report.indicators  // audit mode = show all in detail
    : report.indicators;

  return (
    <div data-testid="conformite-tab">
      {/* Score header + mode toggle */}
      <div className="bg-[#0b0811] border border-white/10 rounded-xl p-5 mb-5 flex items-center gap-5">
        <div className="w-24 h-24 rounded-full flex items-center justify-center border-4 flex-shrink-0" style={{ borderColor: scoreColor }}>
          <span className="text-3xl font-black" style={{ color: scoreColor }} data-testid="conformite-score">{report.score}</span>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Score global Qualiopi</div>
          <h3 className="text-xl font-black text-white">{report.score_label}</h3>
          <p className="text-xs text-slate-400 mt-1">{report.ok_count} / {report.total_indicators} indicateurs conformes · {report.stats.docs_total} documents · {report.stats.eval_responses} reponses evaluation</p>
        </div>
        <div className="flex flex-col gap-2">
          <Button onClick={() => setAuditMode(!auditMode)} variant="ghost" className={"border text-xs " + (auditMode ? "border-[#f7c935] text-[#f7c935] bg-[#f7c935]/10" : "border-white/10 text-slate-300")} data-testid="audit-mode-toggle">
            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> {auditMode ? "Mode audit ACTIF" : "Mode audit"}
          </Button>
          <Button onClick={exportAudit} variant="ghost" className="border border-white/10 text-xs" data-testid="conformite-export">
            <FileDown className="w-3.5 h-3.5 mr-1" /> Export rapport
          </Button>
          <Button onClick={load} variant="ghost" className="text-xs text-slate-500">Recalculer</Button>
        </div>
      </div>

      {auditMode && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-5" data-testid="audit-banner">
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-purple-300 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <div className="font-bold text-white mb-1">Mode audit Qualiopi actif</div>
              <p className="text-slate-300 leading-relaxed">Controle detaille : chaque indicateur affiche ses exigences, les preuves trouvees et les actions correctives. Exportez le rapport pour votre auditeur.</p>
            </div>
          </div>
        </div>
      )}

      {/* Action items */}
      {(report.action_items || []).length > 0 && (
        <div className="bg-[#14101c] border border-amber-500/30 rounded-xl p-4 mb-5" data-testid="conformite-actions">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h4 className="text-sm font-bold text-white">{report.action_items.length} action{report.action_items.length > 1 ? "s" : ""} recommandee{report.action_items.length > 1 ? "s" : ""}</h4>
          </div>
          <ul className="space-y-2">
            {report.action_items.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-xs" data-testid={"conformite-action-" + i}>
                <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase flex-shrink-0 mt-0.5" style={{ background: SEVERITY_COLORS[a.severity] + "20", color: SEVERITY_COLORS[a.severity] }}>{a.severity}</span>
                <div className="flex-1">
                  <div className="text-slate-200">{a.action}</div>
                  <div className="text-[10px] text-slate-600 mt-0.5">Indicateur {a.indicator}{auditMode && a.auto_fix ? ` · ${a.auto_fix}` : ""}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Indicators */}
      <div className="space-y-2" data-testid="conformite-indicators">
        {filteredIndicators.map((ind) => (
          <div key={ind.code} className="bg-[#0b0811] border border-white/10 rounded-xl p-4" data-testid={"indicator-" + ind.code}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: STATUS_COLORS[ind.status] + "20", color: STATUS_COLORS[ind.status] }}>
                {ind.status === "ok" ? <Check className="w-4 h-4" /> : ind.status === "warn" ? <AlertTriangle className="w-4 h-4" /> : <X className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-bold text-slate-500">{ind.code}</span>
                  <span className="text-[9px] px-1 py-0.5 rounded font-bold uppercase" style={{ background: SEVERITY_COLORS[ind.severity] + "20", color: SEVERITY_COLORS[ind.severity] }}>{ind.severity}</span>
                  <span className="text-[9px] px-1 py-0.5 rounded font-bold uppercase" style={{ background: STATUS_COLORS[ind.status] + "20", color: STATUS_COLORS[ind.status] }}>{STATUS_LABELS[ind.status]}</span>
                </div>
                <div className="text-sm font-bold text-white">{ind.name}</div>
                <div className="text-[10px] text-slate-500 mt-1">Documents requis: {ind.docs_required_types.join(", ")} · {ind.docs_found} trouve{ind.docs_found > 1 ? "s" : ""}</div>
                {ind.issues.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {ind.issues.map((x, j) => <li key={j} className="text-[11px] text-amber-400 flex items-start gap-1"><span>•</span><span>{x}</span></li>)}
                  </ul>
                )}
                {auditMode && (
                  <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-slate-500">
                    <b>Exigence Qualiopi</b> : L'organisme doit fournir les preuves documentaires pour cet indicateur. Rattacher des documents Qualiopi a cette session via l'onglet Documents ou le generateur automatique.
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════
function FormateursTab({ session, api }) {
  const [loading, setLoading] = useState(true);
  const [trainers, setTrainers] = useState([]);
  const [progress, setProgress] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "principal" });
  const [team, setTeam] = useState([]);

  const load = useCallback(async () => {
    try {
      const [t, p, tm] = await Promise.all([
        api.get("/lms/sessions/" + session.session_id + "/trainers"),
        api.get("/lms/sessions/" + session.session_id + "/trainer-progress").catch(() => ({ data: null })),
        api.get("/admin/staff").catch(() => ({ data: [] })),
      ]);
      setTrainers(t.data || []);
      setProgress(p.data);
      setTeam(tm.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [session.session_id]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!form.email && !form.name) { toast.error("Email ou nom requis"); return; }
    try {
      await api.post("/lms/sessions/" + session.session_id + "/trainers", form);
      toast.success("Formateur assigne");
      setShowAdd(false);
      setForm({ email: "", name: "", role: "principal" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Retirer ${t.name} ?`)) return;
    try {
      await api.delete("/lms/sessions/" + session.session_id + "/trainers/" + (t.user_id || t.email));
      toast.success("Formateur retire");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (loading) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>;

  return (
    <div data-testid="formateurs-tab">
      <div className="flex justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-white">{trainers.length} formateur{trainers.length > 1 ? "s" : ""} assigne{trainers.length > 1 ? "s" : ""}</h3>
          {progress && <div className="text-[10px] text-slate-500 mt-0.5">Cap progression : <b className="text-[#f7c935]">{progress.cap_percent}%</b> · {progress.trainer_cap_index}/{progress.total_sequences} etapes debloquees pour les stagiaires</div>}
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#f7c935] text-[#14101c] font-bold text-xs" data-testid="trainer-add-btn"><Plus className="w-3 h-3 mr-1" />Assigner</Button>
      </div>
      {trainers.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-xl text-slate-500 text-sm">Aucun formateur assigne. Ajoutez-en un pour debloquer l'espace formateur.</div>
      ) : (
        <div className="bg-[#0b0811] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5"><tr className="text-left text-[10px] uppercase text-slate-500">
              <th className="px-3 py-2">Nom</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Role</th><th className="px-3 py-2">Progression</th><th></th>
            </tr></thead>
            <tbody>
              {trainers.map((t) => {
                const tp = progress?.trainers?.find((p) => p.user_id === t.user_id);
                return (
                  <tr key={t.assignment_id} className="border-t border-white/5" data-testid={"trainer-row-" + t.assignment_id}>
                    <td className="px-3 py-2 text-white font-semibold">{t.name}</td>
                    <td className="px-3 py-2 text-slate-300">{t.email}</td>
                    <td className="px-3 py-2 text-slate-300"><span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-bold uppercase">{t.role}</span></td>
                    <td className="px-3 py-2 text-slate-300">{tp ? `${tp.progress}%` : <span className="text-slate-500">—</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => remove(t)} className="text-red-400 hover:text-red-300" data-testid={"trainer-del-" + t.assignment_id}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <Dialog open onOpenChange={(o) => { if (!o) setShowAdd(false); }}>
          <DialogContent className="bg-[#0b0811] border-white/10 text-white">
            <DialogHeader><DialogTitle>Assigner un formateur</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Choisir dans l'equipe</label>
                <select value="" onChange={(e) => {
                  const m = team.find((x) => x.user_id === e.target.value);
                  if (m) setForm({ email: m.email, name: m.name, role: "principal" });
                }} className={inputCls + " px-3 py-2 rounded w-full"} data-testid="trainer-team-select">
                  <option value="">— ou saisir manuellement ci-dessous —</option>
                  {team.map((m) => <option key={m.user_id} value={m.user_id}>{m.name} ({m.email})</option>)}
                </select>
              </div>
              <Input placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="trainer-email" />
              <Input placeholder="Nom complet" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="trainer-name" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls + " px-3 py-2 rounded w-full"}>
                <option value="principal">Principal</option>
                <option value="secondaire">Secondaire</option>
              </select>
              <p className="text-[10px] text-slate-500">Le role « formateur » sera automatiquement ajoute au compte utilisateur s'il existe dans votre equipe.</p>
            </div>
            <div className="flex justify-end gap-2 mt-3"><Button variant="ghost" onClick={() => setShowAdd(false)}>Annuler</Button><Button onClick={add} disabled={!form.email && !form.name} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="trainer-save">Assigner</Button></div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
